import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import { hotTopics, topicCategories } from "@/mocks/topics";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = topicCategories.find((cat) => cat.slug === slug);
  const topics = hotTopics
    .filter((topic) => topic.categorySlug === slug)
    .sort((a, b) => b.heat - a.heat);

  if (!category) {
    return (
      <section className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line h-7 w-7 flex items-center justify-center"></i>
        </span>
        <h1 className="font-heading text-2xl font-black text-foreground-950">分类不存在</h1>
        <p className="text-sm text-foreground-600">你访问的分类页面可能已调整，看看其他分类吧。</p>
        <Link
          to="/rank"
          className="mt-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回吃瓜榜单
        </Link>
      </section>
    );
  }

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${category.name} - 黑料瓜网`,
      url: `https://duopingheyi.cn/category/${category.slug}`,
      description: category.desc,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://duopingheyi.cn/" },
        { "@type": "ListItem", position: 2, name: "吃瓜榜单", item: "https://duopingheyi.cn/rank" },
        {
          "@type": "ListItem",
          position: 3,
          name: category.name,
          item: `https://duopingheyi.cn/category/${category.slug}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${category.name}话题列表`,
      itemListElement: topics.map((topic, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: topic.title,
        url: `https://duopingheyi.cn/topic/${topic.id}`,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${category.name} - 黑料瓜网${category.name}热点话题整理`}
        description={`${category.desc}黑料瓜网${category.name}栏目持续更新今日瓜与新瓜速递，实时汇总该分类下的热议话题。`}
        keywords={`${category.name},黑料瓜网${category.name},${category.name}话题,今日瓜,吃瓜榜单`}
        path={`/category/${category.slug}`}
        schema={schema}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "吃瓜榜单", to: "/rank" },
              { label: category.name },
            ]}
          />
          <div className="mt-4 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
              <i className={`${category.icon} h-7 w-7 flex items-center justify-center`}></i>
            </span>
            <div>
              <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-5xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
        <p className="font-label text-sm text-foreground-500">
          该分类共 <span className="font-bold text-primary-600">{topics.length}</span> 条话题
        </p>

        {topics.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {topics.map((topic) => (
              <TopicCard key={topic.id} topic={topic} />
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-500">
            这个分类暂时还没有更新话题，稍后再来看看。
          </div>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-5">
          <span className="font-label text-sm font-bold text-foreground-800">其他分类：</span>
          {topicCategories
            .filter((cat) => cat.slug !== category.slug)
            .map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:bg-secondary-100"
              >
                {cat.name}
              </Link>
            ))}
        </div>
      </section>
    </>
  );
}